import { reportMessage } from '@ks-personal/reporter'

export interface ApiReportPayload {
  url: string
  method: string
  statusCode: number
  durationMs: number
  success: boolean
  errorDetail?: string
}

const API_PATH_PREFIX = '/ai-docer/aivideo/api'
const MAX_ERROR_BODY_LEN = 500

function normalizeApiPath(rawUrl: string): string {
  try {
    const { pathname } = new URL(rawUrl)
    const idx = pathname.indexOf(API_PATH_PREFIX)
    if (idx >= 0) {
      const suffix = pathname.slice(idx + API_PATH_PREFIX.length)
      return suffix || '/'
    }
    const localApi = pathname.indexOf('/api/')
    if (localApi >= 0) {
      return pathname.slice(localApi + 4) || '/'
    }
    return pathname
  } catch {
    return rawUrl
  }
}

function truncate(text: string, max = MAX_ERROR_BODY_LEN): string {
  return text.length <= max ? text : `${text.slice(0, max)}…`
}

/** 从失败响应中解析 error / message 等字段（clone，不影响业务侧读 body） */
async function readApiErrorDetail(response: Response): Promise<string | undefined> {
  try {
    const text = await response.clone().text()
    if (!text) return undefined
    try {
      const json = JSON.parse(text) as Record<string, unknown>
      const parts: string[] = []
      for (const key of ['error', 'message', 'msg', 'code']) {
        const v = json[key]
        if (v !== undefined && v !== null && String(v) !== '') {
          parts.push(`${key}=${String(v)}`)
        }
      }
      if (parts.length > 0) return parts.join('; ')
      return truncate(text)
    } catch {
      return truncate(text)
    }
  } catch {
    return undefined
  }
}

function formatNetworkError(error: unknown): string {
  if (error instanceof Error) return error.message
  return String(error)
}

/** 静默上报 API 请求到 Sentry（经 @ks-personal/reporter），不影响业务 */
function reportApiRequest(payload: ApiReportPayload): void {
  try {
    const path = normalizeApiPath(payload.url)
    const { method, statusCode, durationMs, success, errorDetail } = payload
    const base = `${method} ${path} → ${statusCode} (${durationMs}ms)`
    const summary = success ? base : errorDetail ? `${base}: ${errorDetail}` : base

    reportMessage(summary, success ? 'info' : 'error', {
      extra: {
        method,
        path,
        url: payload.url,
        status_code: statusCode,
        duration_ms: durationMs,
        success,
        ...(errorDetail ? { error_detail: errorDetail } : {}),
      },
      tags: {
        type: 'api.request',
        'http.method': method,
        'http.path': path,
        'http.status_code': String(statusCode),
        'http.success': String(success),
      },
    })
  } catch {
    /* 静默 */
  }
}

/** 带 Sentry 上报的 fetch 包装，不改变原有请求语义 */
export async function trackedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  let url = ''
  let method = 'GET'
  let started = 0

  try {
    url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : input instanceof Request
            ? input.url
            : String(input)
    method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase()
    started = performance.now()
  } catch {
    /* 静默 */
  }

  try {
    const response = await fetch(input, init)
    try {
      let errorDetail: string | undefined
      if (!response.ok) {
        errorDetail = await readApiErrorDetail(response)
      }
      reportApiRequest({
        url,
        method,
        statusCode: response.status,
        durationMs: Math.round(performance.now() - started),
        success: response.ok,
        errorDetail,
      })
    } catch {
      /* 静默 */
    }
    return response
  } catch (error) {
    try {
      reportApiRequest({
        url,
        method,
        statusCode: 0,
        durationMs: Math.round(performance.now() - started),
        success: false,
        errorDetail: formatNetworkError(error),
      })
    } catch {
      /* 静默 */
    }
    throw error
  }
}
